/**
 * @fileoverview Filters body for the Ad Spends page to be injected into FlyingMenu.
 * @module shared/components/filters/extensions/AdSpendsFiltersExtension
 */

import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFilter } from '@/shared/hooks/useFilter'
import { MultiCombobox, type ComboboxOption } from '@/shared/components/filters/MultiCombobox'
import { type Platform } from '@/features/ad-spends/schemas/adSpendsSchema'

interface Props {
  platforms: Platform[]
  onPlatformsChange?: (platforms: Platform[]) => void
}

/**
 * Renders DateRange + Countries + SKUs + Platforms controls.
 * The surrounding card and header are provided by FlyingMenu.
 *
 * @component
 */
export function AdSpendsFiltersExtension({ platforms, onPlatformsChange }: Props): ReactElement {
  const { t } = useTranslation('common')
  const {
    countries,
    setCountries,
    availableCountries,
    isLoadingCountries,
    skus,
    setSkus,
    availableSkus,
    isLoadingSkus,
  } = useFilter()

  const countryOptions: ComboboxOption[] = useMemo(
    () =>
      (availableCountries ?? []).map(c => ({
        value: c.code,
        text: c.name,
      })),
    [availableCountries]
  )

  const skuArray = useMemo(
    () => (Array.isArray(availableSkus) ? availableSkus : []),
    [availableSkus]
  )
  const skuOptions: ComboboxOption[] = useMemo(
    () => skuArray.map(s => ({ value: s.name, text: s.name })),
    [skuArray]
  )

  const platformOptions: ComboboxOption[] = useMemo(
    () => ['META', 'GOOGLE', 'TIKTOK', 'OTHER'].map(p => ({ value: p, text: p })),
    []
  )

  return (
    <>
      <MultiCombobox
        options={countryOptions}
        selectedValues={countries}
        onChange={setCountries}
        placeholder={t('filters.searchCountries')}
        disabled={isLoadingCountries}
        isLoading={isLoadingCountries}
        className="shrink-0 basis-36 sm:basis-40 md:basis-44"
      />

      <MultiCombobox
        options={skuOptions}
        selectedValues={skus}
        onChange={setSkus}
        placeholder={t('filters.searchSkus')}
        disabled={isLoadingSkus}
        isLoading={isLoadingSkus}
        className="shrink-0 basis-40 sm:basis-44 md:basis-48"
      />

      <MultiCombobox
        options={platformOptions}
        selectedValues={platforms}
        onChange={vals => onPlatformsChange?.(vals as Platform[])}
        placeholder={t('filters.selectPlatforms')}
        className="shrink-0 basis-40 sm:basis-44 md:basis-48"
      />
    </>
  )
}
